import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import {
  HOCs,
  Menu,
  MenuList,
  MenuListItem,
  MenuListDivider,
} from '@pearsontechnology/aero-components';
import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';

import { PermissionTypes, UserRoles } from 'constants/enums';
import messages from 'Workspace/messages';
import DropdownButton from './DropdownButton';
import { IconRemove } from './styles';

const { OWNER, CONTRIBUTOR, REVIEWER } = PermissionTypes;
const { enhanceWithPopper } = HOCs;
const ButtonWithPopper = enhanceWithPopper(DropdownButton);

class ActionsDropdown extends Component {
  static getDerivedStateFromProps(props) {
    return {
      currentPermission: ActionsDropdown.getCurrentPermissionFromProps(props),
      permissionsList: ActionsDropdown.getPermissions(props),
      hasDeleteAccess: ActionsDropdown.hasDeleteAccess(props),
    };
  }

  static getCurrentPermissionFromProps({ permissionsList, collaborator }) {
    return permissionsList.find(
      (item) => item.permission === collaborator.permissionLevel
    ) || {};
  }

  static getPermissibleRoles(currentUserPermission, collaborator) {
    switch (currentUserPermission) {
      case OWNER:
        return [OWNER, CONTRIBUTOR, REVIEWER];
      case CONTRIBUTOR:
        return collaborator.permissionLevel !== OWNER ?
          [CONTRIBUTOR, REVIEWER] :
          [];
      case REVIEWER:
      default:
        return [];
    }
  }

  static getPermissions({ permissionsList, currentUserPermission, collaborator }) {
    const currentPermissions = ActionsDropdown.getPermissibleRoles(
      currentUserPermission,
      collaborator
    );

    return ActionsDropdown.applyPermissionsOnList(currentPermissions, permissionsList);
  }

  static applyPermissionsOnList(currentPermissions, permissionList) {
    return permissionList.map((listItem) => ({
      ...listItem,
      disabled: !currentPermissions.includes(listItem.permission),
    }));
  }

  static hasAdminRole(collaborator) {
    const roles = collaborator?.account?.roles || [];
    return roles.includes(UserRoles.ADMIN);
  }

  static hasDeleteAccess({ currentUserPermission, collaborator, currentUser }) {
    if (ActionsDropdown.hasAdminRole(collaborator)) {
      return false;
    }

    let hasDeleteAccess;

    switch (currentUserPermission) {
      case OWNER:
        hasDeleteAccess = true;
        break;
      case CONTRIBUTOR:
        hasDeleteAccess = !(collaborator.permissionLevel === OWNER);
        break;
      case REVIEWER:
        hasDeleteAccess = (collaborator.account && collaborator.account.accountId) === currentUser.accountId;
        break;
      default:
        hasDeleteAccess = false;
        break;
    }

    return hasDeleteAccess;
  }

  state = {
    currentPermission: {},
    permissionsList: [],
    hasDeleteAccess: false,
  };

  onChangePermissionHandler = (permission) => () => {
    const { onChangePermission } = this.props;

    if (permission.disabled) {
      return;
    }

    this.setState({
      currentPermission: { ...permission },
    });

    if (onChangePermission) {
      onChangePermission(permission);
    }
  };

  onDeleteHandler = () => {
    const { onDelete, collaborator } = this.props;

    if (onDelete) {
      onDelete(collaborator);
    }
  };

  render() {
    const { currentPermission, permissionsList, hasDeleteAccess } = this.state;

    return (
      <ButtonWithPopper
        placement={'bottom-end'}
        modifiers={{
          preventOverflow: {
            enabled: false,
          },
          hide: {
            enabled: false,
          },
          flip: { enabled: false },
        }}
        text={intl.formatMessage(messages[`permission${currentPermission.text}`])}
        hidepopperonclick
      >
        <Menu width={130}>
          <MenuList>
            {
              permissionsList.map((item) => (
                <MenuListItem
                  key={item.permission}
                  onClick={this.onChangePermissionHandler(item)}
                  selected={item.permission === currentPermission.permission}
                  disabled={item.disabled}
                >
                  {intl.formatMessage(messages[`permission${item.text}`])}
                </MenuListItem>
              ))
            }
            {
              hasDeleteAccess &&
                <Fragment>
                  <MenuListDivider />
                  <MenuListItem onClick={this.onDeleteHandler}>
                    <IconRemove />
                    {intl.formatMessage(messages.actionRemove)}
                  </MenuListItem>
                </Fragment>
            }
          </MenuList>
        </Menu>
      </ButtonWithPopper>
    );
  }
}

ActionsDropdown.defaultProps = {
  permissionsList: [
    {
      text: 'Owner',
      permission: OWNER,
    },
    {
      text: 'Contributor',
      permission: CONTRIBUTOR,
    },
    {
      text: 'Reviewer',
      permission: REVIEWER,
    },
  ],
};

ActionsDropdown.propTypes = {
  collaborator: PropTypes.object,
  onChangePermission: PropTypes.func,
  onDelete: PropTypes.func,
};

export default ActionsDropdown;
